import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './Button';
import { Input } from './Input';
import { Spinner } from './Spinner';
import { DialogContent, DialogHeader, DialogTitle, DialogDescription } from './Dialog';
import { Plus, Trash2, RefreshCw, Copy, Eye, EyeOff, Shield } from 'lucide-react';
import { toast } from 'sonner';
import superjson from 'superjson';
import { Selectable } from 'kysely';
import { PublishedSites } from '../helpers/schema';

interface SiteSecretsDialogProps {
  site: Selectable<PublishedSites>;
  onClose: () => void;
}

interface SecretMeta {
  id: string;
  keyName: string;
  description: string | null;
  isGlobal?: boolean;
}

export const SiteSecretsDialog: React.FC<SiteSecretsDialogProps> = ({ site, onClose }) => {
  const [secrets, setSecrets] = useState<SecretMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [adding, setAdding] = useState(false);

  const loadSecrets = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`/_api/secrets/list?sessionId=${site.sessionId}`);
      const data = superjson.parse<any>(await res.text());
      if (data.secrets) {
        setSecrets(data.secrets.map((s: any) => ({ ...s, isGlobal: false })));
      }
    } catch (err) {
      toast.error('Failed to load secrets');
    } finally {
      setLoading(false);
    }
  }, [site.sessionId]);

  useEffect(() => {
    loadSecrets();
  }, [loadSecrets]);

  const handleAdd = async () => {
    if (!newKey.trim() || !newValue.trim()) {
      toast.error('Key name and value are required');
      return;
    }

    try {
      setAdding(true);
      const res = await fetch('/_api/secrets/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: superjson.stringify({
          sessionId: site.sessionId,
          keyName: newKey.trim().toUpperCase(),
          value: newValue.trim(),
          description: newDescription.trim() || undefined,
        }),
      });
      const data = superjson.parse<any>(await res.text());
      if (data.error) throw new Error(data.error);
      toast.success(`Secret "${newKey.trim().toUpperCase()}" added`);
      setNewKey('');
      setNewValue('');
      setNewDescription('');
      loadSecrets();
    } catch (err: any) {
      toast.error(err.message || 'Failed to add secret');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (keyName: string) => {
    try {
      const res = await fetch('/_api/secrets/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: superjson.stringify({
          sessionId: site.sessionId,
          keyName,
        }),
      });
      const data = superjson.parse<any>(await res.text());
      if (data.error) throw new Error(data.error);
      toast.success(`Secret "${keyName}" deleted`);
      loadSecrets();
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete secret');
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const res = await fetch('/_api/sites/sync-secrets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: superjson.stringify({ siteId: site.id }),
      });
      const data = superjson.parse<any>(await res.text());
      if (data.error) throw new Error(data.error);
      toast.success(data.message || 'Secrets synced to Cloudflare');
    } catch (err: any) {
      toast.error(err.message || 'Failed to sync secrets');
    } finally {
      setSyncing(false);
    }
  };

  const handleCopyToken = () => {
    if ((site as any).accessToken) {
      navigator.clipboard.writeText((site as any).accessToken);
      toast.success('Access token copied');
    }
  };

  return (
    <DialogContent style={{ maxWidth: 560 }}>
      <DialogHeader>
        <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Shield size={18} />
          Manage Secrets — {site.subdomain}
        </DialogTitle>
        <DialogDescription>
          Environment variables injected into your published site. Global platform secrets (like
          Ollama Cloud API) are included automatically.
        </DialogDescription>
      </DialogHeader>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '1.5rem 0' }}>
          <Spinner />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {/* Existing secrets */}
          {secrets.length === 0 ? (
            <p
              style={{
                color: '#666',
                fontSize: '0.875rem',
                textAlign: 'center',
                padding: '0.5rem',
              }}
            >
              No custom secrets configured. Global platform secrets are still available.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
              {secrets.map((s) => (
                <div
                  key={s.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.375rem 0.5rem',
                    background: '#f8f8f8',
                    borderRadius: 6,
                    fontSize: '0.8125rem',
                  }}
                >
                  <code
                    style={{
                      fontFamily: 'monospace',
                      fontWeight: 600,
                      color: '#1a1a2e',
                      flex: 1,
                    }}
                  >
                    {s.keyName}
                  </code>
                  {s.description && (
                    <span style={{ color: '#555', fontSize: '0.75rem', flex: 1 }}>
                      {s.description}
                    </span>
                  )}
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => handleDelete(s.keyName)}
                    title="Delete secret"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Add new secret */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.375rem',
              borderTop: '1px solid #eee',
              paddingTop: '0.75rem',
            }}
          >
            <div style={{ display: 'flex', gap: '0.375rem' }}>
              <Input
                placeholder="KEY_NAME"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value.toUpperCase())}
                style={{ fontFamily: 'monospace', flex: 1 }}
              />
              <Input
                placeholder="Value"
                type="password"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                style={{ flex: 1 }}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.375rem' }}>
              <Input
                placeholder="Description (optional)"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                style={{ flex: 1 }}
              />
              <Button onClick={handleAdd} disabled={adding || !newKey.trim() || !newValue.trim()}>
                <Plus size={14} />
                {adding ? 'Adding...' : 'Add'}
              </Button>
            </div>
          </div>

          {/* Actions */}
          <div
            style={{
              display: 'flex',
              gap: '0.5rem',
              justifyContent: 'space-between',
              borderTop: '1px solid #eee',
              paddingTop: '0.75rem',
            }}
          >
            <Button variant="outline" onClick={handleSync} disabled={syncing}>
              <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} />
              {syncing ? 'Syncing...' : 'Sync to Cloudflare'}
            </Button>

            {(site as any).accessToken && (
              <Button variant="ghost" onClick={handleCopyToken} title="Copy site access token">
                <Copy size={14} />
                Copy Access Token
              </Button>
            )}
          </div>
        </div>
      )}
    </DialogContent>
  );
};
